import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowSquareOut, FileText, Warning, Trash, Plus } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import TransportPlanner from "@/components/TransportPlanner";
import SiteNotesPanel from "@/components/SiteNotesPanel";
import DepartmentUpload from "@/components/DepartmentUpload";
import AvizeOCR from "@/components/AvizeOCR";
import MontajHubPanel from "@/components/MontajHubPanel";
import ActivityFlowActions from "@/components/ActivityFlowActions";
import SmartInbox from "@/components/SmartInbox";

const ST = { pending: "În așteptare", in_progress: "În lucru", waiting: "În așteptare", returned: "Întors", done: "Finalizat", skipped: "Sărit" };

export default function DepartmentHub() {
  const { dept } = useParams();
  const { hasRole, user } = useAuth();
  const [hub, setHub] = useState(null);
  const [err, setErr] = useState("");
  const [allocMember, setAllocMember] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addProj, setAddProj] = useState("");
  const [addName, setAddName] = useState("");
  const [addDue, setAddDue] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [docSel, setDocSel] = useState([]);
  const [fName, setFName] = useState("");
  const [fType, setFType] = useState("__all");
  const [fUser, setFUser] = useState("__all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fDeptOnly, setFDeptOnly] = useState(false);
  const toggleDocSel = (id) => setDocSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const loadHub = () => api.get(`/departments/${dept}/hub`).then((r) => setHub(r.data)).catch((e) => setErr(e.response?.data?.detail || "Eroare la încărcare"));
  useEffect(() => {
    setHub(null); setErr("");
    loadHub();
    // eslint-disable-next-line
  }, [dept]);

  const canDeleteDoc = true;
  const [projects, setProjects] = useState([]);
  const [selProj, setSelProj] = useState("");
  const [projDocs, setProjDocs] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  useEffect(() => { setSelProj(""); setProjDocs([]); }, [dept]);
  const selCode = projects.find((p) => p.id === selProj)?.code || "proiect";
  const projReq = useRef("");
  const loadProjDocs = (pid) => {
    projReq.current = pid;
    setProjDocs([]);
    if (!pid) { return; }
    const code = projects.find((p) => p.id === pid)?.code || "proiect";
    api.get(`/projects/${pid}/folder`).then((r) => projReq.current === pid && setProjDocs((r.data || []).filter((f) => f.category !== "email").map((f) => ({
      file_id: f.id, filename: f.name, storage_path: f.storage_path, category: f.category,
      project_id: f.project_id, project_code: code, uploaded_by_name: f.uploaded_by_name, created_at: f.created_at, source: "dosar",
    })))).catch(() => {});
  };
  useEffect(() => { loadProjDocs(selProj); /* eslint-disable-next-line */ }, [selProj, projects]);
  const onUploaded = (pid) => { loadHub(); if (pid === selProj) loadProjDocs(pid); else if (pid) setSelProj(pid); };

  const announce = async (kind, label) => {
    const note = window.prompt(`Mențiuni pentru „${label}" (opțional):`, "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/projects/${selProj}/announce`, { kind, note });
      toast.success(`Anunț trimis către: ${(data.notified || []).join(", ") || "—"}`);
      loadHub();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare la anunț"); }
  };

  const allocInternal = async () => {
    if (!selProj || !allocMember) return;
    try {
      const { data } = await api.post(`/departments/${dept}/allocate`, { project_id: selProj, assignee_id: allocMember });
      toast.success(`Alocat intern către: ${data.assignee}`);
      setAllocMember("");
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare la alocare"); }
  };

  const delDoc = async (d) => {    if (!d.project_id || !d.file_id) return;
    if (!window.confirm(`Ștergi documentul „${d.filename}" din dosarul digital?`)) return;
    try {
      await api.delete(`/projects/${d.project_id}/folder/${d.file_id}`);
      toast.success("Document șters din dosarul digital");
      loadHub();
      if (selProj) loadProjDocs(selProj);
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare la ștergere"); }
  };

  const openAdd = () => { setAddProj(selProj || ""); setAddName(""); setAddDue(""); setAddNote(""); setAddOpen(true); };
  const submitAdd = async () => {
    if (!addProj) { toast.error("Alege proiectul"); return; }
    if (!addName.trim()) { toast.error("Scrie denumirea activității"); return; }
    setAddBusy(true);
    try {
      await api.post(`/departments/${dept}/activities`, { project_id: addProj, name: addName.trim(), due_at: addDue || "", note: addNote.trim() });
      toast.success("Activitate adăugată în fluxul proiectului");
      setAddOpen(false);
      loadHub();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare la adăugare"); }
    finally { setAddBusy(false); }
  };
  const delActivity = async (a) => {
    if (!window.confirm(`Ștergi activitatea „${a.node_name}" din fluxul proiectului ${a.project_code || ""}?`)) return;
    try {
      await api.delete(`/departments/${dept}/activities/${a.project_id}/${a.node_id}`);
      toast.success("Activitate ștearsă din flux");
      loadHub();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare la ștergere"); }
  };

  if (err) return <div className="mx-auto max-w-5xl px-6 py-8"><p className="text-sm text-red-400" data-testid="dept-hub-error">{err}</p></div>;
  if (!hub) return <div className="mx-auto max-w-5xl px-6 py-8 text-sm text-muted-foreground">Se încarcă…</div>;
  const s = hub.stats;
  const shownDocs = selProj ? projDocs : (hub.documents || []);
  const shownActs = selProj ? (hub.activities || []).filter((a) => a.project_id === selProj) : (hub.activities || []);
  const docTypes = Array.from(new Set((shownDocs || []).map((d) => d.category).filter(Boolean)));
  const docUsers = Array.from(new Set((shownDocs || []).map((d) => d.uploaded_by_name || d.node_name).filter(Boolean)));
  const filteredDocs = (shownDocs || []).filter((d) => {
    if (fName && !(d.filename || "").toLowerCase().includes(fName.toLowerCase())) return false;
    if (fDeptOnly && d.department !== dept) return false;
    if (fType !== "__all" && d.category !== fType) return false;
    if (fUser !== "__all" && (d.uploaded_by_name || d.node_name) !== fUser) return false;
    if (fFrom && d.created_at && d.created_at.slice(0, 10) < fFrom) return false;
    if (fTo && d.created_at && d.created_at.slice(0, 10) > fTo) return false;
    return true;
  });
  return (
    <div className="mx-auto max-w-5xl px-6 py-8 fade-up" data-testid="dept-hub-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Departamente Highberg</p>
      <h1 className="font-head text-3xl font-extrabold tracking-tighter">{hub.label}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{hub.description}</p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Total", s.total, "text-foreground"], ["În lucru", s.active, "text-sky-300"], ["Întârziate", s.overdue, "text-red-300"], ["Finalizate", s.done, "text-emerald-300"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-border bg-white/[0.02] p-4">
            <div className={`text-2xl font-extrabold ${c}`}>{v}</div><div className="text-xs text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      {hub.shortcuts?.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold">Scurtături</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {hub.shortcuts.map((sc) => (
              <Link key={sc.path} to={sc.path} data-testid={`dept-shortcut-${sc.path.slice(1)}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm hover:border-primary/50 hover:text-primary">
                <ArrowSquareOut size={15} /> {sc.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {(hub.key === "logistica" || hub.key === "productie") && <TransportPlanner />}
      {hub.key === "asistent_vanzari" && <SiteNotesPanel />}
      {hub.key === "montaj" && <MontajHubPanel />}
      {(hub.key === "productie" || hub.key === "achizitii" || hub.key === "gestiune") && <AvizeOCR />}

      <DepartmentUpload dept={hub.key} onUploaded={onUploaded} selectedProject={selProj} onProjectChange={setSelProj} />

      <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-white/[0.02] p-3" data-testid="dept-project-filter">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Filtru proiect (Activități + Documente):</span>
        <Select value={selProj || "all"} onValueChange={(v) => setSelProj(v === "all" ? "" : v)}>
          <SelectTrigger className="w-72" data-testid="dept-docs-project"><SelectValue placeholder="Toate proiectele" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate proiectele departamentului</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {selProj && <button onClick={() => setSelProj("")} className="text-xs text-primary hover:underline" data-testid="dept-filter-clear">Resetează</button>}
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Activități ({shownActs.length}){selProj && <span className="ml-1 text-xs font-normal text-muted-foreground">· doar {selCode}</span>}</h2>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={openAdd} data-testid="dept-add-activity-btn"><Plus size={13} weight="bold" className="mr-1" /> Adaugă activitate</Button>
        </div>
        <div className="mt-2 rounded-lg border border-border bg-white/[0.02] p-3 text-xs text-muted-foreground" data-testid="dept-flow-info">
          Operațiunea a fost transmisă automat către departamentul/departamentele responsabile din flux (notificare in-app și în <b>Bling</b>). Dacă aveți mențiuni/observații, folosiți butonul <b>„Trimite mai departe"</b>. Dacă întâmpinați probleme în legătură cu proiectul, trimiteți explicațiile către conducere folosind butonul <b>„Escaladează"</b>.
        </div>
        {shownActs.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">{selProj ? "Nicio activitate pentru proiectul selectat." : "Nicio activitate în lucru pentru acest departament momentan."}</p>
        ) : (
          <div className="mt-2 overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-xs uppercase text-muted-foreground"><tr>
                <th className="px-4 py-2 text-left">Activitate</th><th className="px-4 py-2 text-left">Proiect</th>
                <th className="px-4 py-2 text-left">Termen</th><th className="px-4 py-2 text-left">Stare</th><th className="px-4 py-2 text-left">Acțiuni</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {shownActs.map((a) => (
                  <tr key={a.node_id} data-testid={`dept-activity-${a.node_id}`}>
                    <td className="px-4 py-2 font-medium">{a.node_name}{a.ad_hoc && <span className="ml-1.5 rounded bg-sky-500/15 px-1 py-0.5 text-[9px] uppercase text-sky-300" title="Activitate adăugată manual">ad-hoc</span>}</td>
                    <td className="px-4 py-2"><Link to={`/projects/${a.project_id}`} className="text-primary hover:underline">{a.project_code || a.project_name}</Link></td>
                    <td className="px-4 py-2 text-muted-foreground">{(a.due_at || "").slice(0, 10) || "—"}</td>
                    <td className="px-4 py-2 text-xs"><span className="inline-flex items-center gap-1">{a.run_status !== "done" && a.due_at && a.due_at.slice(0, 10) < new Date().toISOString().slice(0, 10) && <Warning size={13} className="text-red-400" />}{ST[a.run_status] || a.run_status}</span></td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <ActivityFlowActions projectId={a.project_id} projectCode={a.project_code || a.project_name} fromDept={dept} />
                        {true && (
                          <button onClick={() => delActivity(a)} className="text-muted-foreground hover:text-red-400" title="Șterge activitatea" data-testid={`dept-activity-del-${a.node_id}`}><Trash size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Documente ({filteredDocs.length}{filteredDocs.length !== shownDocs.length ? `/${shownDocs.length}` : ""}){selProj && <span className="ml-1 text-xs font-normal text-muted-foreground">· doar {selCode}</span>}</h2>
          {selProj && docSel.length > 0 && (
            <ActivityFlowActions projectId={selProj} projectCode={selCode} fromDept={dept} initialDocs={docSel} forwardOnly label={`Trimite mai departe (${docSel.length})`} onDone={() => setDocSel([])} />
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="dept-doc-filters">
          <Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Caută în denumire…" className="h-8 w-48" data-testid="dept-doc-f-name" />
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="h-8 w-40" data-testid="dept-doc-f-type"><SelectValue placeholder="Tip" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Toate tipurile</SelectItem>{docTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fUser} onValueChange={setFUser}>
            <SelectTrigger className="h-8 w-44" data-testid="dept-doc-f-user"><SelectValue placeholder="Adăugat de" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">Toți utilizatorii</SelectItem>{docUsers.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="h-8 w-36" data-testid="dept-doc-f-from" title="De la data" />
          <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="h-8 w-36" data-testid="dept-doc-f-to" title="Până la data" />
          {!selProj && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground" data-testid="dept-doc-f-deptonly">
              <input type="checkbox" checked={fDeptOnly} onChange={(e) => setFDeptOnly(e.target.checked)} className="accent-primary" />
              Doar documentele acestui departament
            </label>
          )}
          {(fName || fType !== "__all" || fUser !== "__all" || fFrom || fTo || fDeptOnly) && <button onClick={() => { setFName(""); setFType("__all"); setFUser("__all"); setFFrom(""); setFTo(""); setFDeptOnly(false); }} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="dept-doc-f-clear">Resetează filtre</button>}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{selProj ? "Documentele proiectului selectat. Poți trimite mai departe chiar și fără document încărcat." : "Documentele din dosarul digital ale proiectelor departamentului tău. Alege un proiect din filtrul de mai sus ca să vezi doar activitățile și documentele lui."}</p>
        {selProj && (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/[0.05] px-3 py-2" data-testid="dept-project-actions">
            <span className="text-xs text-muted-foreground">Acțiuni pentru <b>{selCode}</b>:</span>
            <ActivityFlowActions projectId={selProj} projectCode={selCode} fromDept={dept} />
            {dept === "financiar" && <Button size="sm" variant="secondary" onClick={() => announce("avans_incasat", "Încasare avans")} data-testid="announce-avans">Anunță încasare avans</Button>}
            {(dept === "vanzari" || dept === "asistent_vanzari") && <Button size="sm" variant="secondary" onClick={() => announce("lansare", "Lansare proiect")} data-testid="announce-lansare">Anunță lansarea proiectului</Button>}
            {hub.is_coordinator && (
              <div className="flex items-center gap-2" data-testid="alloc-internal-box">
                <span className="text-xs text-muted-foreground">Alocare internă:</span>
                <Select value={allocMember} onValueChange={setAllocMember}>
                  <SelectTrigger className="h-8 w-52" data-testid="alloc-member"><SelectValue placeholder="Alege persoana…" /></SelectTrigger>
                  <SelectContent>{(hub.members || []).filter((m) => m.id !== user?.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" onClick={allocInternal} disabled={!allocMember} data-testid="alloc-send">Alocă</Button>
              </div>
            )}
          </div>
        )}
        {filteredDocs.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">{shownDocs.length ? "Niciun document nu corespunde filtrelor." : (selProj ? "Niciun document în acest proiect încă." : "Niciun document încă.")}</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-xs uppercase text-muted-foreground"><tr>
                {selProj && <th className="px-3 py-2"></th>}
                <th className="px-4 py-2 text-left">Document</th><th className="px-4 py-2 text-left">Proiect</th>
                <th className="px-4 py-2 text-left">Adăugat de</th><th className="px-4 py-2 text-left">Acțiuni</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {filteredDocs.map((d, i) => (
                  <tr key={i} data-testid={`dept-doc-${i}`}>
                    {selProj && <td className="px-3 py-2">{d.file_id && <Checkbox checked={docSel.includes(d.file_id)} onCheckedChange={() => toggleDocSel(d.file_id)} data-testid={`dept-doc-sel-${i}`} />}</td>}
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <FileText size={15} className="shrink-0 text-blue-400" />
                        <span className="font-medium">{d.filename}</span>
                        {d.source === "dosar" && d.category && <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{d.category}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2">{d.project_id ? <Link to={`/projects/${d.project_id}`} className="text-primary hover:underline">{d.project_code || "proiect"}</Link> : "—"}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{d.uploaded_by_name || d.node_name || "—"}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {(d.storage_path || d.url) && <a href={d.storage_path ? fileUrl(d.storage_path) : d.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline" data-testid={`dept-doc-open-${i}`}>Deschide</a>}
                        {d.project_id && <ActivityFlowActions projectId={d.project_id} projectCode={d.project_code} fromDept={dept} />}
                        {canDeleteDoc && d.source === "dosar" && d.file_id && (
                          <button onClick={() => delDoc(d)} className="text-muted-foreground hover:text-red-400" title="Șterge documentul" data-testid={`dept-doc-del-${i}`}><Trash size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <SmartInbox department={dept} showAssistant={false} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adaugă activitate în flux — {hub.label}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-xs text-muted-foreground">Adaugă o activitate ad-hoc în fluxul unui proiect (pentru cazuri speciale, în afara fluxului clasic). Apare în lista de activități a departamentului, cu „Trimite mai departe" și „Escaladează".</p>
            <div className="grid gap-1.5">
              <Label className="text-xs">Proiect</Label>
              <Select value={addProj} onValueChange={setAddProj}>
                <SelectTrigger data-testid="add-activity-project"><SelectValue placeholder="Alege proiectul…" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Denumire activitate</Label>
              <Input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="ex: Verificare suplimentară cu clientul" data-testid="add-activity-name" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Termen (opțional)</Label>
              <Input type="date" value={addDue} onChange={(e) => setAddDue(e.target.value)} data-testid="add-activity-due" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Mențiuni (opțional)</Label>
              <Textarea value={addNote} onChange={(e) => setAddNote(e.target.value)} placeholder="Detalii despre activitate…" data-testid="add-activity-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Anulează</Button>
            <Button onClick={submitAdd} disabled={addBusy} data-testid="add-activity-save">{addBusy ? "Se adaugă…" : "Adaugă activitate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
